import React, { useCallback, useEffect, useMemo } from 'react'
import { useI18n } from 'contexts/AuthContext'
import ContentHeader from './ContentHeader'
import ConfirmedAppointment from './ConfirmedAppointment'
import LoadingDialog, { LoadingContent } from 'components/LoadingDialog/index'
import { logBrazeEvent } from 'utils/brazeInit'
import { useAppointment } from 'hooks/useAppointment'
import { useApplyNewPatientTag } from 'hooks/useApplyTags'
import ScheduleContent from 'components/ScheduleContent/index'
import { ScreenKey } from './util'

export const MaxLimitedDate = 56

const getScheduleContent = (sku) => {
  switch (sku) {
    case 'msk':
    case 'pt':
      return {
        topIcon: null,
        title: 'Schedule a call with a physical therapist',
        subtitle: 'Get expert help on your recovery journey',
      }
    default:
      return {
        topIcon: null,
        title: 'Schedule a call with a registered dietician',
        subtitle:
          'Achieve your goals faster with expert help guiding you on your journey',
      }
  }
}

const ScheduledTime = (props) => {
  const { sku } = props
  const { i18n } = useI18n()
  const { applyNewPatientTag } = useApplyNewPatientTag()

  const qualification = useMemo(() => {
    switch (sku) {
      case 'msk':
      case 'pt':
        return 'pt'
      default:
        return 'rd'
    }
  }, [sku])

  const {
    selectedAppointmentType,
    selectedProviderIds,
    selectedSlot,
    setSelectedSlot,
    bookedAppointment,
    setBookedAppointment,
    payLoading,
    setPayLoading,
    bookLoading,
    setBookPayLoading,
    providerIds,
    loadingVisible,
    setLoadingVisible,
    appointmentsCount,
    onCardPay,
    providerId,
    completeBookingAppointment,
    isLoading,
    scheduleLanguage,
    onLanguageChange,
    selectedLanguage,
    setScheduleLanguage,
    logScheduleClickEvent,
  } = useAppointment({ ...props, qualification })

  const isRD = useMemo(() => {
    return sku === 'rd' || !sku
  }, [sku])

  const showSkip = useMemo(() => {
    return sku === 'wlc' || sku === 'msk'
  }, [sku])

  const hideTimeZone = useMemo(() => {
    return ['msk', 'wlc', 'pt'].includes(sku)
  }, [sku])

  useEffect(() => {
    if (appointmentsCount === 0 && bookedAppointment && isRD) {
      applyNewPatientTag()
      logBrazeEvent('Schedule Appointment End')
    }
  }, [bookedAppointment, appointmentsCount, isRD])

  useEffect(() => {
    if (bookedAppointment) {
      props.setRemoveEventListener(true)
    }
  }, [bookedAppointment, props])

  const onBack = useCallback(
    (e) => {
      if (bookedAppointment) {
        setBookedAppointment(null)
      } else {
        props.onBack()
      }
    },
    [bookedAppointment]
  )

  const contentInfo = useMemo(() => {
    return getScheduleContent(sku)
  }, [sku])

  if (bookedAppointment) {
    return (
      <ConfirmedAppointment
        bookedAppointment={bookedAppointment}
        appointmentsCount={appointmentsCount}
        scheduleLanguage={scheduleLanguage}
        {...props}
        onBack={onBack}
      />
    )
  }

  return (
    <div className='info-sec'>
      <ContentHeader {...props} onBack={onBack} hideBack={isRD} />
      <div className='info-content'>
        {isLoading ? (
          <div className='loading-content'>
            <LoadingContent />
          </div>
        ) : (
          <>
            {contentInfo.topIcon && (
              <img
                src={contentInfo.topIcon}
                alt=''
                className='top-icon'
                style={{ marginTop: '15px' }}
              />
            )}
            <div
              className='title'
              style={contentInfo.topIcon ? { marginTop: '10px' } : {}}
            >
              {i18n(contentInfo.title)}
            </div>
            <div
              className='subtitle'
              style={{
                marginBottom: '28px',
                marginTop: '10px',
                marginLeft: '0px',
                marginRight: '0px',
              }}
            >
              {i18n(contentInfo.subtitle)}
            </div>
          </>
        )}
        <ScheduleContent
          {...{
            selectedAppointmentType,
            providerId,
            selectedProviderIds,
            providerIds,
            selectedSlot,
            setSelectedSlot,
            isLoading,
            setPayLoading,
            setBookPayLoading,
            completeBookingAppointment,
            setBookedAppointment,
            payLoading,
            bookLoading,
            onCardPay,
            hideTimeZone,
            showSkip,
            onSkip: () => {
              switch (sku) {
                case 'msk':
                case 'pt':
                  props.setCurrentKey(ScreenKey.REHAB_CONGRATULATIONS)
                  break
                case 'wlc':
                  props.setCurrentKey(ScreenKey.SKU_CONGRATULATIONS)
                  break
                default:
                  break
              }
            },
            onLanguageChange,
            selectedLanguage,
            setScheduleLanguage,
            logScheduleClickEvent,
          }}
        />
      </div>
      <LoadingDialog
        visible={loadingVisible}
        setVisible={setLoadingVisible}
        center={true}
      />
    </div>
  )
}

export default ScheduledTime
